import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Tabs, message } from 'antd';
import { FN110, n202Api } from '../../services/n202Api';
import { useN202Mode } from './hooks/useN202Mode';
import { useN202Record } from './hooks/useN202Record';
import { useN202Navigation } from './hooks/useN202Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import { SharedActionBar } from '../../components/SharedActionBar';
import { isFieldDisabledFn } from './helpers';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Vendor1Details from './components/Tab2Vendor1Details';
import Tab3Vendor2Details from './components/Tab3Vendor2Details';
import Tab4Vendor3Details from './components/Tab4Vendor3Details';
import Tab5ReceiptDetails from './components/Tab5ReceiptDetails';
import Tab6Messages from './components/Tab6Messages';
import Tab7Attachments from './components/Tab7Attachments';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import './N202Form.css';
import { BatchAcceptanceBridge, pwn202f1AcceptanceManifest } from '../../features/acceptance';

const N202Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState('1');
  const [showHistory, setShowHistory] = useState(false);

  // Hooks
  const perm = useModulePermission('pwn202f1');
  const { mode, setMode, isEditing } = useN202Mode();
  const {
    record, formData, details, loading,
    loadRecord, setField, getField,
    setDetailTable,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
  } = useN202Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useN202Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecord(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load N202 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecord, setCurrentIdx]);

  // isFieldDisabled helper
  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode],
  );

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FN110) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fn110_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab('1');
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除客訴單 ${record.fn110_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecord(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecord, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Save action for SharedActionBar
  const handleSaveAction = useCallback(async () => {
    await handleAddAction();
  }, [handleAddAction]);

  // Cancel action for SharedActionBar
  const handleCancelAction = useCallback(() => {
    revertToRecord();
    setMode('browse');
  }, [revertToRecord, setMode]);

  // ── Approval Handlers ──
  const handleSubmit = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定送審此客訴單「${record.fn110_01}」？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          await n202Api.submitForApproval(record.fn110_01);
          message.success('已送審');
          await loadRecord(record.fn110_01);
        } catch {
          message.error('送審失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handleApprove = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定核准此客訴單「${record.fn110_01}」？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          await n202Api.approve(record.fn110_01);
          message.success('已核准');
          await loadRecord(record.fn110_01);
        } catch {
          message.error('核准失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handleReject = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定退回此客訴單「${record.fn110_01}」？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await n202Api.reject(record.fn110_01);
          message.success('已退回');
          await loadRecord(record.fn110_01);
        } catch {
          message.error('退回失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handleWriteoff = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fn110_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await n202Api.writeoff(record.fn110_01);
          message.success('沖銷成功');
          await loadRecord(record.fn110_01);
        } catch {
          message.error('沖銷失敗');
        }
      },
    });
  }, [record, loadRecord]);

  // 審核狀態：fn110_80 是 boolean（不同於其他模組的 string '_80' 欄位）
  // true = 已核准 → 映射為 'P'，false = 未核准 → 映射為 ''
  // SharedActionBar 期望 string statusCode，所以做 boolean → string 轉換
  // 舊 FoxPro 資料匯入時 boolean 值直接對應，不會有中間狀態漏掉
  const approvalStatusCode: string = record?.fn110_80 ? 'P' : '';

  // Tab items
  const tabItems = [
    {
      key: '1',
      label: '基本資料',
      children: (
        <Tab1BasicInfo
          formData={formData}
          isEditing={isEditing}
          isFieldDisabled={isFieldDisabled}
          getField={getField}
          setField={setField}
        />
      ),
    },
    {
      key: '2',
      label: '廠商一客訴原因',
      children: (
        <Tab2Vendor1Details
          formData={formData}
          rows={details.vendor1Reasons}
          isEditing={isEditing}
          onRowsChange={updater => setDetailTable('vendor1Reasons', updater)}
          onVendorFieldChange={setField}
          isFieldDisabled={isFieldDisabled}
        />
      ),
    },
    {
      key: '3',
      label: '廠商二客訴原因',
      children: (
        <Tab3Vendor2Details
          formData={formData}
          rows={details.vendor2Reasons}
          isEditing={isEditing}
          onRowsChange={updater => setDetailTable('vendor2Reasons', updater)}
          onVendorFieldChange={setField}
          isFieldDisabled={isFieldDisabled}
        />
      ),
    },
    {
      key: '4',
      label: '廠商三客訴原因',
      children: (
        <Tab4Vendor3Details
          formData={formData}
          rows={details.vendor3Reasons}
          isEditing={isEditing}
          onRowsChange={updater => setDetailTable('vendor3Reasons', updater)}
          onVendorFieldChange={setField}
          isFieldDisabled={isFieldDisabled}
        />
      ),
    },
    {
      key: '5',
      label: '入庫明細',
      children: (
        <Tab5ReceiptDetails
          formData={formData}
          rows={details.receiptDetails}
          isEditing={isEditing}
          onRowsChange={updater => setDetailTable('receiptDetails', updater)}
          onMasterFieldChange={setField}
          isFieldDisabled={isFieldDisabled}
        />
      ),
    },
    {
      key: '6',
      label: '留言傳送',
      children: (
        <Tab6Messages
          formData={formData}
          rows={details.messages}
          isEditing={isEditing}
          onRowsChange={updater => setDetailTable('messages', updater)}
        />
      ),
    },
    {
      key: '7',
      label: '附件清單',
      children: (
        <Tab7Attachments
          formData={formData}
          rows={details.attachments}
          isEditing={isEditing}
          onRowsChange={updater => setDetailTable('attachments', updater)}
        />
      ),
    },
  ];

  // Render
  return (
    <div className="n202-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>品質管理</a>
              <span>/</span>
              <span className="current">客訴處理記錄單-採購</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>客訴處理記錄單-採購</h1>
            <span className="badge badge-default">N202</span>
          </div>
        </div>

        {/* Record info bar */}
        <div className="record-info-bar">
          <span>
            <span className="info-label">單號:</span>
            <span className="info-value">{formData.fn110_01 || '(新增)'}</span>
          </span>
          <span>
            <span className="info-label">記錄:</span>
            <span className="info-value">
              {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '0 / 0'}
            </span>
          </span>
          <span>
            <span className="info-label">模式:</span>
            <span className="info-value">
              {mode === 'browse' ? '瀏覽' : mode === 'add' ? '新增' : '編輯'}
            </span>
          </span>
        </div>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
          size="small"
        />

        {/* Bottom Action Bar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => handleNavTo(0)}
          onNavPrev={() => handleNavTo(currentIdx - 1)}
          onNavNext={() => handleNavTo(currentIdx + 1)}
          onNavLast={() => handleNavTo(allIds.length - 1)}
          onAdd={() => { setMode('add'); resetForAdd(); setActiveTab('1'); }}
          onModify={() => { if (!record) return; setMode('edit'); }}
          onDelete={handleDeleteAction}
          onSave={handleSaveAction}
          onCancel={handleCancelAction}
          approval={{
            statusCode: approvalStatusCode,          // boolean→string 映射結果
            isApproved: approvalStatusCode === 'P',  // true 時鎖定 + 顯示「已核准」
            isPending: approvalStatusCode === 'D',   // 送審中（統一碼 D，自創碼 S 已廢除；批次 5）
            isLocked: ['P', 'D'].includes(approvalStatusCode),
          }}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onReject={handleReject}
          onWriteoff={handleWriteoff}
          onHistory={() => setShowHistory(true)}
          onExit={() => navigate('/erp')}
          canCreate={perm.canCreate}
          canEdit={perm.canEdit}
          canDelete={perm.canDelete}
          canApprove={perm.canApprove}
          acceptanceActionIds={{
            create: 'PWN202-ACT-CREATE',
            update: 'PWN202-ACT-UPDATE',
            delete: 'PWN202-ACT-DELETE',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwn202f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fn110_01 ? [record.fn110_01] : []}
          keyOf={(item) => item.fn110_01}
          queryExact={(key) => n202Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />

        {/* Approval History Modal */}
        <ApprovalHistoryModal
          visible={showHistory}
          orderNo={record?.fn110_01 || ''}
          onClose={() => setShowHistory(false)}
        />
      </div>
    </div>
  );
};

export default N202Form;
